import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { SEO } from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProductCard from '@/components/ProductCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useInfiniteProducts, useProductBrands } from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useInView } from 'react-intersection-observer';

type Gender = 'female' | 'male' | 'unisex';

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'female', label: 'Mujer' },
  { value: 'male', label: 'Hombre' },
  { value: 'unisex', label: 'Unisex' },
];

const Catalog = () => {
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const [selectedGender, setSelectedGender] = useState<Gender | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 400);

  const { data: categories } = useCategories();
  const { data: brands } = useProductBrands();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromQuery = params.get('category');
    if (categoryFromQuery) {
      setSelectedCategorySlug(categoryFromQuery);
    }
  }, [location.search]);

  const selectedCategoryId = useMemo(() => {
    if (!selectedCategorySlug || !categories) return null;
    const match = categories.find((c) => (c.slug || c.$id) === selectedCategorySlug);
    return match?.$id ?? null;
  }, [selectedCategorySlug, categories]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteProducts(12, {
    categoryId: selectedCategoryId,
    gender: selectedGender,
    brand: selectedBrand,
    search: debouncedSearch,
  });
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const selectCategory = (slug: string | null) => {
    setSelectedCategorySlug(slug);
    const params = new URLSearchParams(location.search);
    if (slug) params.set('category', slug);
    else params.delete('category');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
  };

  const clearFilters = () => {
    selectCategory(null);
    setSelectedGender(null);
    setSelectedBrand(null);
    setSearchInput('');
  };

  const products = data?.pages.flatMap((page) => page.documents) ?? [];
  const totalResults = data?.pages[0]?.total ?? 0;
  const hasActiveFilters = Boolean(selectedCategorySlug || selectedGender || selectedBrand || debouncedSearch);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Catálogo"
        description="Explorá nuestra colección completa de fragancias y joyería de autor. Filtrá por categoría, género o marca y encontrá tu fragancia perfecta en Tyche."
        url="/catalogo"
      />
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4 block">
              Catálogo
            </span>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
              Todos nuestros productos
            </h1>
            <div className="line-gold w-24 mx-auto mb-6" />
            <p className="font-body text-muted-foreground leading-relaxed">
              Explora nuestra colección completa de perfumes y joyas exclusivas.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-md mx-auto mb-10 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por nombre..."
              className="pl-9"
            />
          </div>

          {/* Mobile filters: horizontal scroll chips */}
          <div className="lg:hidden space-y-4 mb-8">
            <div className="flex gap-2 overflow-x-auto pb-2">
              <button
                onClick={() => selectCategory(null)}
                className={`shrink-0 px-4 py-2 rounded-full font-body text-sm transition-colors ${
                  selectedCategorySlug === null ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                Todas las categorías
              </button>
              {categories?.map((category) => (
                <button
                  key={category.slug || category.$id}
                  onClick={() => selectCategory(category.slug || category.$id)}
                  className={`shrink-0 px-4 py-2 rounded-full font-body text-sm transition-colors ${
                    selectedCategorySlug === (category.slug || category.$id)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {GENDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedGender(selectedGender === option.value ? null : option.value)}
                  className={`shrink-0 px-4 py-2 rounded-full font-body text-sm transition-colors ${
                    selectedGender === option.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {brands?.map((brand) => (
                <button
                  key={brand}
                  onClick={() => setSelectedBrand(selectedBrand === brand ? null : brand)}
                  className={`shrink-0 px-4 py-2 rounded-full font-body text-sm transition-colors ${
                    selectedBrand === brand ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {brand}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-center mb-6 max-w-2xl mx-auto lg:max-w-none">
            <span className="text-sm text-muted-foreground">
              {totalResults} resultado{totalResults === 1 ? '' : 's'}
            </span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Limpiar filtros
              </Button>
            )}
          </div>

          <div className="flex gap-8">
            {/* Desktop sidebar */}
            <aside className="hidden lg:block w-56 shrink-0 sticky top-24 self-start space-y-8">
              <div>
                <h3 className="font-body text-sm font-semibold uppercase tracking-wide mb-3">Categoría</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => selectCategory(null)}
                    className={`block w-full text-left px-3 py-2 rounded-md text-sm ${
                      selectedCategorySlug === null ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    Todas
                  </button>
                  {categories?.map((category) => (
                    <button
                      key={category.slug || category.$id}
                      onClick={() => selectCategory(category.slug || category.$id)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-sm ${
                        selectedCategorySlug === (category.slug || category.$id)
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-body text-sm font-semibold uppercase tracking-wide mb-3">Género</h3>
                <div className="space-y-1">
                  {GENDER_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedGender(selectedGender === option.value ? null : option.value)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-sm ${
                        selectedGender === option.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-body text-sm font-semibold uppercase tracking-wide mb-3">Marca</h3>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {brands?.map((brand) => (
                    <button
                      key={brand}
                      onClick={() => setSelectedBrand(selectedBrand === brand ? null : brand)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-sm ${
                        selectedBrand === brand ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      {brand}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                {products.map((product, index) => {
                  const activeCategory = selectedCategoryId
                    ? product.categories?.find((c) => c.$id === selectedCategoryId) ?? product.categories?.[0]
                    : product.categories?.[0];

                  const categoryName = activeCategory?.name ?? 'Sin categoría';
                  const categoryDesc = product.description || activeCategory?.description || '';

                  return (
                    <div
                      key={product.slug || product.$id}
                      className="opacity-0 animate-fade-in"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <Link to={`/producto/${product.slug}`}>
                        <ProductCard
                          name={product.name}
                          description={categoryDesc}
                          category={categoryName}
                          image={product.image_url}
                          slug={product.slug}
                        />
                      </Link>
                    </div>
                  );
                })}
              </div>

              <div ref={ref} className="text-center py-8">
                {isFetchingNextPage && (
                  <div className="text-muted-foreground">Cargando más productos...</div>
                )}
                {!hasNextPage && products.length > 0 && (
                  <div className="text-muted-foreground">No hay más productos</div>
                )}
                {products.length === 0 && !isLoading && !isFetchingNextPage && (
                  <div className="text-muted-foreground space-y-4">
                    <p>No se encontraron productos con estos filtros.</p>
                    {hasActiveFilters && (
                      <Button variant="outline" size="sm" onClick={clearFilters}>
                        Limpiar filtros
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Catalog;
